<template>
  <el-dialog
    :visible="visible"
    width="700px"
    :close-on-click-modal="false"
    :show-close="!submitting"
    custom-class="modify-bags-dialog"
    @close="close"
  >
    <div slot="title" class="modify-bags-dialog-title">
      <i class="fas fa-boxes modify-bags-title-icon"></i>
      {{ i18nValues['modifyBags'] || 'Modificar Bolsas' }}
    </div>

    <div v-loading="loadingDetail" style="min-height: 80px;">
      <div v-if="labelBatch">

        <!-- Summary section -->
        <div class="modify-bags-section">
          <div class="modify-bags-row">
            <div class="modify-bags-row-icon"><i class="fas fa-seedling"></i></div>
            <div class="modify-bags-row-label">{{ i18nValues['product'] || 'Producto' }}</div>
            <div class="modify-bags-row-value">
              <el-tag size="small" type="info" effect="plain">{{ productName }}</el-tag>
            </div>
          </div>
          <div class="modify-bags-row">
            <div class="modify-bags-row-icon"><i class="fas fa-boxes"></i></div>
            <div class="modify-bags-row-label">{{ i18nValues['currentBags'] || 'Bolsas Actuales' }}</div>
            <div class="modify-bags-row-value">
              <el-tag size="small" type="info" effect="plain">{{ currentBagsNumber }}</el-tag>
            </div>
          </div>
          <div class="modify-bags-row">
            <div class="modify-bags-row-icon"><i class="fas fa-balance-scale"></i></div>
            <div class="modify-bags-row-label">{{ i18nValues['weightPerBag'] || 'Peso por Bolsa (Kg)' }}</div>
            <div class="modify-bags-row-value">
              <el-tag size="small" type="info" effect="plain">{{ currentWeightPerBag }} Kg</el-tag>
            </div>
          </div>
          <div class="modify-bags-row">
            <div class="modify-bags-row-icon"><i class="fas fa-weight-hanging"></i></div>
            <div class="modify-bags-row-label">{{ i18nValues['grossWeight'] || 'Peso Bruto (Kg)' }}</div>
            <div class="modify-bags-row-value">
              <el-tag size="small" type="info" effect="plain">{{ currentGrossWeight }} Kg</el-tag>
            </div>
          </div>
          <div class="modify-bags-row">
            <div class="modify-bags-row-icon"><i class="fas fa-box"></i></div>
            <div class="modify-bags-row-label">{{ i18nValues['bagType'] || 'Tipo de Bolsa' }}</div>
            <div class="modify-bags-row-value">
              <el-tag size="small" type="info" effect="plain">{{ currentTypeLabel }}</el-tag>
            </div>
          </div>
          <div class="modify-bags-row">
            <div class="modify-bags-row-icon"><i class="fas fa-weight"></i></div>
            <div class="modify-bags-row-label">{{ i18nValues['seedPerLb'] || 'Peso por Mil (g)' }}</div>
            <div class="modify-bags-row-value">
              <el-tag size="small" type="info" effect="plain">{{ currentSeedPerLb || '-' }}</el-tag>
            </div>
          </div>
        </div>

        <!-- Labels and Pallets table -->
        <div v-if="labels.length > 0" class="modify-bags-table-section">
          <div class="modify-bags-table-header">
            <i class="fas fa-tag"></i>
            {{ i18nValues['labelsAndPallets'] || 'Etiquetas y Pallets afectados' }}
          </div>
          <el-table :data="labels" stripe size="small" max-height="250">
            <el-table-column prop="id" :label="i18nValues['labelId'] || 'Etiqueta ID'" width="100" align="center"></el-table-column>
            <el-table-column :label="i18nValues['pallet'] || 'Pallet'" width="100" align="center">
              <template slot-scope="scope">
                {{ scope.row.pallet ? scope.row.pallet.id : '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="i18nValues['bags'] || 'Bolsas'" width="80" align="center">
              <template slot-scope="scope">
                {{ scope.row.pallet ? scope.row.pallet.bagsNumber : '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="i18nValues['netWeight'] || 'Peso Neto (Kg)'" width="120" align="right">
              <template slot-scope="scope">
                {{ scope.row.pallet ? scope.row.pallet.netWeight : '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="i18nValues['grossWeight'] || 'Peso Bruto (Kg)'" width="120" align="right">
              <template slot-scope="scope">
                {{ scope.row.pallet ? scope.row.pallet.grossWeight : '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="i18nValues['originSilo'] || 'Silo Origen'" width="120" align="center">
              <template slot-scope="scope">
                <span v-if="getOriginSilo(scope.row)">{{ getOriginSilo(scope.row).name || getOriginSilo(scope.row).id }}</span>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- Modification form -->
        <div class="modify-bags-form-section">
          <div class="modify-bags-table-header">
            <i class="fas fa-edit"></i>
            {{ i18nValues['modification'] || 'Modificación' }}
          </div>
          <div style="padding: 14px;">
            <el-form :model="form" label-position="top" size="small">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item :label="i18nValues['newBagsNumber'] || 'Nueva Cantidad de Bolsas'">
                    <el-input
                      v-model.number="form.newBagsNumber"
                      type="number"
                      :min="1"
                      :max="9999"
                      @input="recalculate"
                    ></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item :label="i18nValues['newWeightPerBag'] || 'Nuevo Peso por Bolsa (Kg)'">
                    <el-input
                      v-model.number="form.newWeightPerBag"
                      type="number"
                      :min="0.001"
                      @input="recalculate"
                    ></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item :label="i18nValues['newBagType'] || 'Nuevo Tipo de Bolsa'">
                    <el-select
                      v-model="form.newType"
                      :placeholder="i18nValues['bagType'] || 'Tipo de Bolsa'"
                      style="width: 100%;"
                    >
                      <el-option value="bag" :label="i18nValues['bag'] || 'Paper Bags'"></el-option>
                      <el-option value="bigBag" :label="i18nValues['bigBag'] || 'Bulk Bag'"></el-option>
                      <el-option value="box" :label="i18nValues['box'] || 'Box'"></el-option>
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item :label="i18nValues['newSeedPerLb'] || 'Nuevo Peso por Mil (g)'">
                    <el-input
                      v-model.number="form.newSeedPerLb"
                      type="number"
                      :min="0.001"
                    ></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
              <template v-if="needsSilo">
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item :label="i18nValues['destinationSilo'] || 'Silo Destino'">
                      <el-select
                        v-model="form.siloId"
                        :placeholder="i18nValues['selectSilo'] || 'Seleccionar Silo'"
                        :disabled="(!siloMismatch && silos.length === 1) || hasMixedProducts"
                        filterable
                        style="width: 100%;"
                      >
                        <el-option
                          v-for="silo in silos"
                          :key="silo.id"
                          :label="getSiloLabel(silo)"
                          :value="silo.id"
                        ></el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                </el-row>

                <div v-if="siloMismatch" class="modify-bags-warning">
                  <i class="fas fa-exclamation-circle" style="color: #f56c6c;"></i>
                  <span>{{ i18nValues['siloProductMismatch'] || 'El silo de origen tiene un producto diferente. Seleccione otro silo.' }}</span>
                </div>

                <el-row v-if="selectedSiloHasNoField" :gutter="20">
                  <el-col :span="12">
                    <el-form-item :label="i18nValues['assignField'] || 'Campo para el Silo'">
                      <el-select
                        v-model="form.fieldId"
                        :placeholder="i18nValues['selectField'] || 'Seleccionar Campo'"
                        filterable
                        style="width: 100%;"
                      >
                        <el-option
                          v-for="f in fields"
                          :key="f.id"
                          :label="f.name || ('Campo ' + f.id)"
                          :value="f.id"
                        ></el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                </el-row>
                <div v-if="selectedSiloHasNoField" class="modify-bags-field-hint">
                  <i class="fas fa-exclamation-triangle" style="color: #909399;"></i>
                  <span>{{ i18nValues['siloNoField'] || 'El silo seleccionado no tiene Campo. Seleccione uno para asociarlo.' }}</span>
                </div>
              </template>
            </el-form>
          </div>
        </div>

        <!-- Calculation preview -->
        <div v-if="hasChanges" class="modify-bags-preview-section">
          <div class="modify-bags-table-header">
            <i class="fas fa-calculator"></i>
            {{ i18nValues['preview'] || 'Vista Previa' }}
          </div>
          <div class="modify-bags-preview-row">
            <span class="modify-bags-preview-label">{{ i18nValues['newNetWeight'] || 'Nuevo Peso Neto' }}</span>
            <span class="modify-bags-preview-value"><strong>{{ newNetWeight }}</strong> Kg</span>
          </div>
          <div class="modify-bags-preview-row">
            <span class="modify-bags-preview-label">{{ i18nValues['newGrossWeight'] || 'Nuevo Peso Bruto' }}</span>
            <span class="modify-bags-preview-value"><strong>{{ newGrossWeight }}</strong> Kg</span>
          </div>
          <div v-if="weightChanged" class="modify-bags-preview-row">
            <span class="modify-bags-preview-label">{{ kgDifference > 0 ? (i18nValues['surplus'] || 'Sobrante') : (i18nValues['deficit'] || 'Faltante') }}</span>
            <span class="modify-bags-preview-value">
              <i :class="kgDifference > 0 ? 'fas fa-arrow-left' : 'fas fa-arrow-right'" style="font-size: 10px; margin-right: 6px; color: #909399;"></i>
              <strong>{{ Math.abs(kgDifferenceTotal) }}</strong> Kg ({{ labels.length }} pallets)
            </span>
          </div>
          <div v-if="typeChanged" class="modify-bags-preview-row">
            <span class="modify-bags-preview-label">{{ i18nValues['newBagType'] || 'Tipo de Bolsa' }}</span>
            <span class="modify-bags-preview-value">{{ currentTypeLabel }} <i class="fas fa-arrow-right" style="font-size: 10px; margin: 0 6px; color: #909399;"></i> <strong>{{ newTypeLabel }}</strong></span>
          </div>
          <div v-if="seedChanged" class="modify-bags-preview-row">
            <span class="modify-bags-preview-label">{{ i18nValues['seedsPerPound'] || 'Semillas/Libra (SPP)' }}</span>
            <span class="modify-bags-preview-value">{{ currentSeedsPerPound || '-' }} <i class="fas fa-arrow-right" style="font-size: 10px; margin: 0 6px; color: #909399;"></i> <strong>{{ newSeedsPerPound }}</strong></span>
          </div>
        </div>

        <!-- Mixed products warning -->
        <el-alert
          v-if="hasMixedProducts"
          :title="i18nValues['mixedProductsWarning'] || 'No se puede modificar: esta etiqueta tiene Items con Productos o Peso por Bolsa distintos.'"
          type="error"
          show-icon
          :closable="false"
          style="margin-top: 12px;"
        ></el-alert>

      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="close" size="small" :disabled="submitting">{{ i18nValues['cancel'] || 'Cancelar' }}</el-button>
      <el-button
        type="primary"
        size="small"
        :disabled="!canSubmit"
        :loading="submitting"
        icon="fas fa-check"
        @click="submit"
      >
        {{ i18nValues['confirm'] || 'Confirmar' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { get } from "vuex-pathify"; // eslint-disable-line no-unused-vars

export default {
  name: "LabelBagModificationModal",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    labelBatchId: {
      type: Number,
      default: null,
    },
  },

  computed: {
    globalFieldSite: get("globalFieldSite"),
    user: get("authentication/user"),

    productName() {
      if (!this.labelBatch || !this.labelBatch.items || !this.labelBatch.items.length) return '-';
      var item = this.labelBatch.items[0];
      return item.product ? (item.product.brand + ' - ' + item.product.name) : '-';
    },

    batchName() {
      if (!this.labelBatch || !this.labelBatch.items || !this.labelBatch.items.length) return '-';
      return this.labelBatch.items[0].batch || '-';
    },

    currentBagsNumber() {
      if (!this.labelBatch || !this.labelBatch.items || !this.labelBatch.items.length) return 0;
      return this.labelBatch.items[0].bagsNumber || 0;
    },

    weightPerBag() {
      if (!this.labelBatch || !this.labelBatch.items || !this.labelBatch.items.length) return 0;
      return this.labelBatch.items[0].weightPerBag || 0;
    },

    currentWeightPerBag() {
      return this.weightPerBag;
    },

    effectiveWeightPerBag() {
      return (this.form.newWeightPerBag && this.form.newWeightPerBag > 0) ? this.form.newWeightPerBag : this.weightPerBag;
    },

    currentGrossWeight() {
      if (!this.labelBatch) return 0;
      return this.labelBatch.grossWeight || 0;
    },

    currentType() {
      if (!this.labelBatch || !this.labelBatch.items || !this.labelBatch.items.length) return null;
      return this.labelBatch.items[0].type || null;
    },

    currentTypeLabel() {
      return this.getTypeLabel(this.currentType);
    },

    newTypeLabel() {
      return this.getTypeLabel(this.form.newType);
    },

    currentSeedPerLb() {
      if (!this.labelBatch || !this.labelBatch.items || !this.labelBatch.items.length) return null;
      return this.labelBatch.items[0].seedPerLb || null;
    },

    currentSeedsPerPound() {
      if (!this.labelBatch || !this.labelBatch.items || !this.labelBatch.items.length) return null;
      return this.labelBatch.items[0].seedsPerPound || null;
    },

    newSeedsPerPound() {
      var spl = this.form.newSeedPerLb;
      if (!spl || spl <= 0) return this.currentSeedsPerPound || '-';
      return Math.round(453600 / spl);
    },

    newNetWeight() {
      if (!this.form.newBagsNumber || !this.effectiveWeightPerBag) return 0;
      return Math.round(this.form.newBagsNumber * this.effectiveWeightPerBag * 100) / 100;
    },

    newGrossWeight() {
      var oldNet = this.currentBagsNumber * this.weightPerBag;
      var oldTara = this.currentGrossWeight - oldNet;
      var newTara = oldTara;
      if (this.typeChanged) {
        var oldBagTara = this.getBagTara(this.currentType);
        var newBagTara = this.getBagTara(this.form.newType);
        newTara = oldTara - oldBagTara + newBagTara;
      }
      return Math.round((this.newNetWeight + newTara) * 100) / 100;
    },

    kgDifference() {
      var oldNet = this.currentBagsNumber * this.weightPerBag;
      return Math.round((oldNet - this.newNetWeight) * 100) / 100;
    },

    kgDifferenceTotal() {
      return Math.round(this.kgDifference * this.labels.length * 100) / 100;
    },

    weightChanged() {
      var bagsChanged = this.form.newBagsNumber && this.form.newBagsNumber !== this.currentBagsNumber;
      var wpbChanged = this.form.newWeightPerBag && Math.abs(this.form.newWeightPerBag - this.weightPerBag) > 0.001;
      return bagsChanged || wpbChanged;
    },

    typeChanged() {
      return this.form.newType && this.form.newType !== this.currentType;
    },

    seedChanged() {
      if (!this.form.newSeedPerLb || this.form.newSeedPerLb <= 0) return false;
      if (!this.currentSeedPerLb) return true;
      return Math.abs(this.form.newSeedPerLb - this.currentSeedPerLb) > 0.001;
    },

    siloMismatch() {
      if (!this.originSilo || !this.labelBatch || !this.labelBatch.items || !this.labelBatch.items.length) return false;
      var item = this.labelBatch.items[0];
      if (!item.product || !this.originSilo.product) return false;
      return item.product.id !== this.originSilo.product.id;
    },

    hasMixedProducts() {
      if (!this.labelBatch || !this.labelBatch.items || this.labelBatch.items.length <= 1) return false;
      var refItem = this.labelBatch.items[0];
      var refProductId = refItem.product ? refItem.product.id : null;
      var refWpb = refItem.weightPerBag || 0;
      for (var i = 1; i < this.labelBatch.items.length; i++) {
        var item = this.labelBatch.items[i];
        var productId = item.product ? item.product.id : null;
        if (productId !== refProductId) return true;
        if (Math.abs((item.weightPerBag || 0) - refWpb) > 0.001) return true;
      }
      return false;
    },

    selectedSiloHasNoField() {
      if (!this.form.siloId) return false;
      var selected = this.silos.find(s => s.id === this.form.siloId);
      return selected && !selected.field;
    },

    hasChanges() {
      return this.weightChanged || this.typeChanged || this.seedChanged;
    },

    needsSilo() {
      return this.weightChanged;
    },

    canSubmit() {
      if (!this.hasChanges || this.submitting || this.hasMixedProducts) return false;
      if (this.form.newBagsNumber == null || this.form.newBagsNumber <= 0) return false;
      if (this.effectiveWeightPerBag <= 0) return false;
      if (this.needsSilo && !this.form.siloId) return false;
      if (this.needsSilo && this.selectedSiloHasNoField && !this.form.fieldId) return false;
      return true;
    },
  },

  watch: {
    visible(val) {
      if (val && this.labelBatchId) {
        this.loadData();
      }
    },
    'form.siloId'() {
      this.form.fieldId = null;
      if (this.selectedSiloHasNoField) {
        this.loadFields();
      }
    },
  },

  data() {
    return {
      labelBatch: null,
      labels: [],
      silos: [],
      fields: [],
      originSilo: null,
      loadingDetail: false,
      submitting: false,
      form: {
        newBagsNumber: null,
        newWeightPerBag: null,
        newSeedPerLb: null,
        newType: null,
        siloId: null,
        fieldId: null,
      },
    };
  },

  methods: {
    loadData() {
      this.loadingDetail = true;
      this.labelBatch = null;
      this.labels = [];
      this.silos = [];
      this.originSilo = null;
      this.form.newBagsNumber = null;
      this.form.newWeightPerBag = null;
      this.form.newSeedPerLb = null;
      this.form.newType = null;
      this.form.siloId = null;
      this.form.fieldId = null;
      this.fields = [];

      var promises = [
        this.$store.dispatch("label/retrieveLabelBatchById", { id: this.labelBatchId }),
        this.$store.dispatch("label/retrieveLabelsWithPallets", { labelBatchId: this.labelBatchId }),
      ];

      Promise.all(promises)
        .then((results) => {
          this.labelBatch = results[0];
          this.labels = results[1] || [];

          // Set current bags as initial value
          if (this.labelBatch && this.labelBatch.items && this.labelBatch.items.length) {
            var refItem = this.labelBatch.items[0];
            this.form.newBagsNumber = refItem.bagsNumber;
            this.form.newWeightPerBag = refItem.weightPerBag;
            this.form.newSeedPerLb = refItem.seedPerLb || null;
            this.form.newType = refItem.type || null;
          }

          // Find origin silo from the first label's pallet items
          this.originSilo = this.findOriginSilo();

          // Load silos for the field site
          return this.loadSilos();
        })
        .finally(() => {
          this.loadingDetail = false;
        });
    },

    findOriginSilo() {
      for (var i = 0; i < this.labels.length; i++) {
        var label = this.labels[i];
        if (label.pallet && label.pallet.items) {
          for (var j = 0; j < label.pallet.items.length; j++) {
            var item = label.pallet.items[j];
            if (item.originSilo) {
              return item.originSilo;
            }
          }
        }
      }
      return null;
    },

    loadSilos() {
      if (!this.globalFieldSite || !this.globalFieldSite.id) return;

      // If we have an origin silo and the product matches, just use that silo
      if (this.originSilo && !this.siloMismatch) {
        this.silos = [this.originSilo];
        this.form.siloId = this.originSilo.id;
        return;
      }

      // Product mismatch: load all silos and filter to same product or empty silos
      return this.$store
        .dispatch("silo/retrieveSilos", { id: this.globalFieldSite.id })
        .then((res) => {
          var allSilos = res || [];
          var productId = this.labelBatch && this.labelBatch.items && this.labelBatch.items.length
            ? (this.labelBatch.items[0].product ? this.labelBatch.items[0].product.id : null)
            : null;
          // Filter: exclude discard silos, then keep silos with same product or empty
          this.silos = allSilos.filter(function(silo) {
            // Exclude discard silos
            if (silo.type === 'discard') return false;
            var siloProductId = silo.product ? silo.product.id : null;
            var isEmpty = !silo.usedKg || silo.usedKg === 0;
            // Allow silos with same product, or empty silos with no product assigned
            return (productId && siloProductId === productId) || (isEmpty && !siloProductId);
          });
          // Don't pre-select origin silo since it has wrong product
        });
    },

    getOriginSilo(label) {
      if (!label.pallet || !label.pallet.items) return null;
      for (var i = 0; i < label.pallet.items.length; i++) {
        if (label.pallet.items[i].originSilo) {
          return label.pallet.items[i].originSilo;
        }
      }
      return null;
    },

    getSiloLabel(silo) {
      var label = silo.name || ('Silo ' + silo.id);
      if (silo.product) {
        label += ' (' + (silo.product.brand || '') + ' - ' + (silo.product.name || '') + ')';
      }
      if (silo.field) {
        label += ' - ' + (silo.field.name || silo.field.id);
      }
      if (silo.usedKg != null) {
        label += ' [' + silo.usedKg + ' Kg]';
      }
      return label;
    },

    getTypeLabel(type) {
      var map = {
        'bag': this.i18nValues['bag'] || 'Paper Bags',
        'bigBag': this.i18nValues['bigBag'] || 'Bulk Bag',
        'box': this.i18nValues['box'] || 'Box',
      };
      return map[type] || type || '-';
    },

    getBagTara(type) {
      if (type === 'bag') return 18.5;
      if (type === 'bigBag') return 15.0;
      return 0.0;
    },

    recalculate() {
      // Computed properties handle recalculation automatically
    },

    loadFields() {
      this.$store.dispatch("field/retrieveFields").then((res) => {
        this.fields = res || [];
      });
    },

    submit() {
      var msg = 'Se modificarán ' + this.labels.length + ' pallets.';
      if (this.weightChanged) {
        msg = 'Se modificarán ' + this.labels.length + ' pallets de ' + this.currentBagsNumber + ' a ' + this.form.newBagsNumber + ' bolsas. ' +
          (this.kgDifference > 0 ? 'Se devolverán ' : 'Se retirarán ') + Math.abs(this.kgDifferenceTotal) + ' Kg ' +
          (this.kgDifference > 0 ? 'al' : 'del') + ' silo.';
      }
      msg += ' ¿Continuar?';

      this.$confirm(
        this.i18nValues['confirmModifyBags'] || msg,
        this.i18nValues['confirm'] || 'Confirmar Modificación',
        {
          confirmButtonText: this.i18nValues['confirm'] || 'Confirmar',
          cancelButtonText: this.i18nValues['cancel'] || 'Cancelar',
          type: 'warning',
        }
      ).then(() => {
        this.submitting = true;
        var request = {
          labelBatchId: this.labelBatchId,
          newBagsNumber: this.form.newBagsNumber,
          newWeightPerBag: this.form.newWeightPerBag,
          newSeedPerLb: this.seedChanged ? this.form.newSeedPerLb : null,
          newType: this.typeChanged ? this.form.newType : null,
          siloId: this.form.siloId || null,
          fieldId: this.form.fieldId || null,
          modificationUser: this.user ? this.user.name : null,
        };

        this.$store.dispatch("label/modifyBags", request)
          .then((res) => {
            if (res && res.status === 'OK') {
              this.$notify.success({
                title: this.i18nValues['success'] || 'Éxito',
                message: res.statusDescription || (this.i18nValues['bagsModified'] || 'Bolsas modificadas correctamente'),
                position: 'bottom-right',
                duration: 5000,
              });
              this.$emit('modified');
              this.close();
            } else {
              this.$notify.error({
                title: this.i18nValues['error'] || 'Error',
                message: (res && res.statusDescription) || (this.i18nValues['errorModifyBags'] || 'Error al modificar bolsas'),
                position: 'bottom-right',
                duration: 7000,
              });
            }
          })
          .finally(() => {
            this.submitting = false;
          });
      }).catch(() => {
        // User cancelled
      });
    },

    close() {
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style scoped>
::v-deep .el-dialog__body {
  overflow: hidden;
}

/* Dialog title */
.modify-bags-dialog-title {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
}

.modify-bags-title-icon {
  margin-right: 8px;
  color: #909399;
  font-size: 16px;
}

/* Summary section (card style) */
.modify-bags-section {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

.modify-bags-row {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #f2f3f5;
}

.modify-bags-row:last-child {
  border-bottom: none;
}

.modify-bags-row:hover {
  background: #fafafa;
}

.modify-bags-row-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #f4f4f5;
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
  margin-right: 12px;
}

.modify-bags-row-label {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.modify-bags-row-value {
  flex-shrink: 0;
}

/* Table section */
.modify-bags-table-section {
  margin-top: 18px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

.modify-bags-table-header {
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modify-bags-table-header i {
  color: #909399;
}

/* Form section */
.modify-bags-form-section {
  margin-top: 18px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

/* Warning message (inline) */
.modify-bags-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 6px;
  margin: 10px 0 0 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
}

/* Field hint (no background) */
.modify-bags-field-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0 0 0;
  margin: 0;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}

/* Preview section */
.modify-bags-preview-section {
  margin-top: 18px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

.modify-bags-preview-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 14px;
  border-bottom: 1px solid #f2f3f5;
  font-size: 13px;
}

.modify-bags-preview-row:last-child {
  border-bottom: none;
}

.modify-bags-preview-label {
  color: #606266;
  font-weight: 500;
}

.modify-bags-preview-value {
  color: #303133;
  font-size: 13px;
}
</style>
